// Redux, Redux-Sagas
import { all, call, cancelled, fork, put, take, takeLatest } from 'redux-saga/effects';

import { eventChannel } from 'redux-saga';

// Firebase
import { auth, authTools } from 'config/firebase';

// Actions, Action Types
import {
    CHECK_USER_AUTHENTICATED,
    EXECUTE_RESET_PASSWORD_FLIGHT,
    LOGIN_USER_EMAIL_PASSWORD,
    LOGOUT_USER,
    VERIFY_ACTION_CODE
} from '../actions/types';

import {
    actionCodeValidationFailure,
    actionCodeValidationSuccess,
    loginUserFailure,
    logoutUserSuccess,
    resetPasswordFailure,
    resetPasswordSuccess,
    userIsAuthenticated,
    userNotAuthenticated
} from '../actions/Auth';

// Constants
import { errorMessage } from '../../utils/constants';

import { userCollectionWatch } from './User';

// Loggers
import { log } from '../../utils/Loggers';
import { platformMaintenanceWatch } from './PlatformMaintenance';

////////////////////////////////////// Checking/Watching for Authenticated User ///////////////////////////////////////

export function* userAuthWatch() {
    let unsubscribeAuth;

    const userAuthChannel = eventChannel(emit => {
        unsubscribeAuth = authTools.onAuthStateChanged(auth, user => {
            if (user) {
                user.exists = true;
                emit({ user });
            } else {
                user = { exists: false };
                emit({ user });
            }
        });
        return unsubscribeAuth;
    });

    try {
        while (true) {
            const authenticated = yield take(userAuthChannel);

            // disable maintenanceBanner
            sessionStorage.setItem('maintenanceBannerDismissed', 'false');
            if (authenticated.user.exists === true) {
                yield put(userIsAuthenticated(authenticated.user));
                yield fork(userCollectionWatch, authenticated.user);

                yield fork(platformMaintenanceWatch);
            } else {
                yield put(userNotAuthenticated());
            }
        }
    } catch (error) {
        console.error('Auth Error: Getting User Auth channel', { error });
    } finally {
        unsubscribeAuth();
        if (yield cancelled()) {
            userAuthChannel.close();
            unsubscribeAuth();
        }
    }
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/////////////////////////////////////////// Log In With Email and Password ////////////////////////////////////////////

export const loginUserEmailPasswordRequest = async (email, password, operator) =>
    operator
        ? { authenticated: true }
        : await authTools
              .signInWithEmailAndPassword(auth, email, password)
              .then(response => {
                  return { authenticated: response };
              })
              .catch(error => {
                  return { error };
              });

export function* loginUserEmailPassword({ payload }) {
    const { email, password, isManagerRegistration, isResiRegistration, suppressRedirect } = payload;
    const { authenticated, error } = yield call(() =>
        loginUserEmailPasswordRequest(email, password)
    );

    if (authenticated) {
        yield put(userIsAuthenticated(authenticated.user));
        yield fork(userCollectionWatch, authenticated.user);

        if (isResiRegistration) {
            sessionStorage.setItem('resi-registration-verify-identity', 'true');
            window.location.href = '/verify-identity';
            return;
        }

        if (!isManagerRegistration && !suppressRedirect) {
            window.location.href = '/';
        }
    } else {
        // Same message for every failure so the response doesn't reveal whether an account exists
        const errorObj = {
            error,
            message: errorMessage.invalidCredentials
        };
        log('Auth Error: signing in with username & password', {
            error,
            email
        });
        yield put(loginUserFailure(errorObj));
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////// Reset User Password Request Const and Func /////////////////////////////////////

const verifyActionCodeRequest = oobCode => {
    return authTools
        .verifyPasswordResetCode(auth, oobCode)
        .then(email => {
            return { validCode: email };
        })
        .catch(error => {
            return { error };
        });
};

function* verifyActionCode({ payload }) {
    const { oobCode } = payload;
    const { validCode, error } = yield call(verifyActionCodeRequest, oobCode);
    if (validCode) {
        yield put(actionCodeValidationSuccess());
    } else {
        console.error('Reset Password Link Error: Link has expired or is invalid', {
            error,
            oobCode
        });
        yield put(actionCodeValidationFailure());
    }
}

const resetUserPasswordRequest = async (oobCode, password) => {
    return authTools
        .confirmPasswordReset(auth, oobCode, password)
        .then(() => {
            return { resetPassword: true };
        })
        .catch(error => {
            return { error };
        });
};

function* resetUserPassword({ payload }) {
    const { oobCode, password } = payload;
    const { resetPassword, error } = yield call(
        resetUserPasswordRequest,
        oobCode,
        password
    );
    if (resetPassword) {
        yield put(resetPasswordSuccess());
    } else {
        console.error('Reset Password Error: Unsuccessful password reset', {
            error,
            oobCode,
            password
        });
        yield put(resetPasswordFailure());
    }
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

///////////////////////////////// Create User With Email and Password Const and Func ///////////////////////////////////

// const createUserWithEmailPasswordRequest = async (email, password) =>
//   await auth.createUserWithEmailAndPassword(email, password)
//     .then(authUser => authUser)
//     .catch(error => error);

// function* createUserWithEmailPassword({ payload }) {
//   const { email, password } = payload;
//   try {
//     const signUpUser = yield call(createUserWithEmailPasswordRequest, email, password);
//     if (signUpUser.message) {
//       // yield put(showAuthMessage(signUpUser.message));
//     } else {
//       yield put(userSignUpSuccess(signUpUser));
//     }
//   } catch (error) {
//     console.error(error)
//     // yield put(showAuthMessage(error));
//   }
// }

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

///////////////////////////////////////////// Log User Out ////////////////////////////////////////////////

const loggingUserOutRequest = async () =>
    await authTools
        .signOut(auth)
        .then(() => true)
        .catch(error => error);

function* loggingUserOut() {
    try {
        const logoutUser = yield call(loggingUserOutRequest);
        if (!logoutUser?.message) {
            yield put(logoutUserSuccess());
        }
    } catch (error) {
        console.error('Auth Error: User unsuccessful in logging out', {
            error
        });
    }
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export async function verifyAuth(email, password) {
    try {
        const auth = await authTools.getAuth();
        const currentAuthUser = auth.currentUser;

        if (!currentAuthUser) {
            throw new Error('Auth link may be expired.');
        } else {
            const credential = await authTools.EmailAuthProvider.credential(
                email,
                password
            );
            return authTools
                .reauthenticateWithCredential(currentAuthUser, credential)
                .then(res => {
                    return res;
                })
                .catch(err => {
                    console.error(err);
                    return err;
                });
        }
    } catch (error) {
        console.error(error);
        return error;
    }
}

/////////////////////////////////////////// Function Generators For Root Saga /////////////////////////////////////////

export function* getUserAuth() {
    yield takeLatest(CHECK_USER_AUTHENTICATED, userAuthWatch);
}

export function* loginUser() {
    yield takeLatest(LOGIN_USER_EMAIL_PASSWORD, loginUserEmailPassword);
}

// export function* createUserAccount() {
//   yield takeEvery(SIGNUP_USER, createUserWithEmailPassword);
// }

export function* logOutUser() {
    yield takeLatest(LOGOUT_USER, loggingUserOut);
}

export function* verifyingActionCode() {
    yield takeLatest(VERIFY_ACTION_CODE, verifyActionCode);
}

export function* resettingUserPassword() {
    yield takeLatest(EXECUTE_RESET_PASSWORD_FLIGHT, resetUserPassword);
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////// Root Saga //////////////////////////////////////////////////////

export default function* rootSaga() {
    yield all([
        fork(loginUser),
        fork(getUserAuth),
        fork(logOutUser),
        fork(verifyingActionCode),
        fork(resettingUserPassword)
    ]);
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
